<template>
  <section class="px-3 py-4 sm:px-5 sm:py-6 lg:px-8">
    <div class="mx-auto max-w-6xl">
      <header class="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-indigo-50 px-5 py-6 sm:px-8 sm:py-8">
        <div class="flex min-w-0 items-center gap-3">
          <HistoryBackButton />
          <div class="min-w-0">
            <h1 class="m-0 text-xl font-semibold text-slate-950 sm:text-2xl">Checkins</h1>
            <p class="m-0 mt-1 text-sm text-slate-500">Your places, visits, and moments along the way.</p>
          </div>
        </div>
        <span v-if="!loading && !error && checkins.length" class="shrink-0 rounded-full bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-blue-700">
          {{ checkins.length }} {{ checkins.length === 1 ? 'visit' : 'visits' }}
        </span>
      </header>

      <div v-if="loading" class="mt-5" aria-label="Loading checkins">
        <div class="mb-3 flex items-center gap-2.5" aria-hidden="true">
          <span class="h-7 w-7 animate-pulse rounded-full bg-blue-100"></span>
          <span class="h-4 w-24 animate-pulse rounded-full bg-slate-200"></span>
        </div>
        <div class="checkin-grid">
          <div v-for="index in 6" :key="index" class="checkin-grid-item" aria-hidden="true">
            <div class="grid animate-pulse gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div class="flex items-center gap-2.5 rounded-xl bg-slate-50 p-2.5">
                <span class="h-9 w-9 rounded-xl bg-slate-200"></span>
                <span class="grid flex-1 gap-2"><span class="h-3.5 w-2/3 rounded-full bg-slate-200"></span><span class="h-3 w-5/6 rounded-full bg-slate-100"></span></span>
              </div>
              <span class="h-3.5 rounded-full bg-slate-100"></span>
              <span class="h-3.5 w-4/5 rounded-full bg-slate-100"></span>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="error" class="mt-5 rounded-2xl bg-red-50 px-4 py-6 text-center text-sm font-semibold text-red-700">
        <p class="m-0">{{ error }}</p>
        <button class="mt-3 rounded-full bg-white px-4 py-2 text-xs font-semibold shadow-sm" type="button" @click="loadCheckins">Try again</button>
      </div>

      <div v-else-if="!checkins.length" class="mt-5 rounded-2xl border border-slate-200 bg-white py-12 text-center shadow-sm">
        <div class="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-blue-50 text-blue-600"><i class="ri-map-pin-user-line text-3xl" aria-hidden="true"></i></div>
        <p class="m-0 mt-4 text-lg font-semibold text-slate-900">No checkins posted</p>
        <p class="mt-2 text-sm text-slate-500">Your business checkins will appear here.</p>
      </div>

      <div v-else class="checkin-groups mt-5">
        <section v-for="group in checkinGroups" :key="group.key" class="checkin-group">
          <header class="checkin-group-heading">
            <span class="checkin-group-icon"><i class="ri-calendar-check-line" aria-hidden="true"></i></span>
            <h2>{{ group.label }}</h2>
            <span class="checkin-group-count">{{ group.items.length }}</span>
          </header>

          <div class="checkin-grid">
            <div v-for="checkin in group.items" :key="checkin.id" class="checkin-grid-item">
              <CheckinCard :checkin="checkin" />
            </div>
          </div>
        </section>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useHead } from '@vueuse/head'
import api from '@/services/api'
import CheckinCard from '@/components/common/CheckinCard.vue'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'
import { useSessionStore } from '@/stores/session'
import { parseApiDate } from '@/utils/date'
import { checkinText } from '@/utils/checkin'

useHead({
  title: 'Checkins | NetFlyr',
  meta: [
    { name: 'description', content: 'View your NetFlyr checkins and business visits, including locations, comments, dates, and local activity history connected to nearby places and community discovery.' },
    { name: 'keywords', content: 'NetFlyr checkins, business checkins, local visits, community activity, place checkins, checkin history' }
  ]
})

const session = useSessionStore()
const checkins = ref([])
const loading = ref(true)
const error = ref('')

const flattenGroups = (value) => value && typeof value === 'object'
  ? Object.values(value).flatMap((items) => Array.isArray(items) ? items : [])
  : []

const extractRows = (payload) => {
  if (Array.isArray(payload)) return payload
  const candidates = [payload?.list, payload?.data, payload?.result, payload?.checkins, payload?.checkin_list, payload?.business_checkins, payload?.data?.list, payload?.data?.checkins]
  return candidates.find(Array.isArray) || flattenGroups(candidates.find((value) => value && typeof value === 'object'))
}

const normalizeCheckin = (item = {}, index) => ({
  ...item,
  id: item.id || item.checkin_id || `checkin-${item.business_id || index}-${index}`,
  flyer_type: 'checkin',
  review_comment: checkinText(item.comments, item.review_comment),
  business_name: checkinText(item.s_business, item.business_name, item.title),
  business_adress: checkinText(item.s_address, item.business_adress, item.business_address, item.address),
  business_lat: item.s_latit ?? item.business_lat ?? '',
  business_long: item.s_longit ?? item.business_long ?? '',
  created_at: checkinText(item.checkin_date, item.created_at)
})

const checkinGroups = computed(() => {
  const groups = new Map()
  const sortedCheckins = [...checkins.value].sort((first, second) => {
    const firstTime = parseApiDate(first.created_at)?.getTime() || 0
    const secondTime = parseApiDate(second.created_at)?.getTime() || 0
    return secondTime - firstTime
  })

  sortedCheckins.forEach((checkin) => {
    const date = parseApiDate(checkin.created_at)
    const key = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}` : 'recent'
    const label = date
      ? new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date)
      : 'Other visits'
    if (!groups.has(key)) groups.set(key, { key, label, items: [] })
    groups.get(key).items.push(checkin)
  })

  return Array.from(groups.values())
})

const loadCheckins = async () => {
  const userId = session.userId || session.user?.user_id || ''
  if (!userId) {
    loading.value = false
    error.value = 'Checkins could not be loaded.'
    return
  }
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.listCheckins({ user_id: userId })
    checkins.value = extractRows(data).map(normalizeCheckin)
  } catch (requestError) {
    checkins.value = []
    error.value = requestError?.response?.data?.message || 'Checkins could not be loaded.'
  } finally {
    loading.value = false
  }
}

onMounted(loadCheckins)
</script>

<style scoped>
.checkin-groups { display: grid; gap: 2rem; }
.checkin-group { min-width: 0; }
.checkin-group-heading { display: flex; align-items: center; gap: 0.65rem; margin-bottom: 1rem; }
.checkin-group-heading::after { content: ''; height: 1px; flex: 1; background: rgb(226 232 240); margin-left: 0.5rem; }
.checkin-group-heading h2 { margin: 0; color: rgb(30 41 59); font-size: 0.95rem; font-weight: 700; }
.checkin-group-icon { display: grid; width: 2rem; height: 2rem; place-items: center; border-radius: 0.65rem; background: rgb(239 246 255); color: rgb(37 99 235); }
.checkin-group-count { color: rgb(100 116 139); font-size: 0.8rem; }
.checkin-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 1rem; align-items: start; }
.checkin-grid-item { min-width: 0; }
@media (min-width: 768px) {
  .checkin-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
